import { Search } from "lucide-react";
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router";
import { useTypedSelector } from "@/store/store";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";
import { ProjectCard } from "./ProjectCard";
import { ProjectDeleteModal } from "./ProjectDeleteModal";
import { ProjectEditModal } from "./ProjectEditModal";
import WelcomeModal from "./WelcomeModal";

export const Projects = () => {
  const [search, setSearch] = useState("");
  const [, setSearchParams] = useSearchParams();

  const projectIdsByLastUpdated = useTypedSelector(
    (state) => state.supabaseStore.projectIdsByLastUpdated,
  );

  const baseProjects = useTypedSelector(
    (state) => state.supabaseStore.projects,
  );
  const projectsLoadStatus = useTypedSelector(
    (state) => state.supabaseStore.projectsLoadStatus,
  );

  const clearSelection = useIssueSelectionStore(
    (state) => state.clearSelection,
  );

  const projectList = useMemo(() => {
    if (
      !projectIdsByLastUpdated ||
      !Object.keys(projectIdsByLastUpdated).length
    ) {
      return Object.values(baseProjects).map((project) => ({
        project,
        lastUpdated: null,
      }));
    }

    return projectIdsByLastUpdated
      .map(({ id, most_recent_update }) => ({
        project: baseProjects[id],
        lastUpdated: most_recent_update,
      }))
      .filter(({ project }) => {
        return Boolean(project);
      });
  }, [projectIdsByLastUpdated, baseProjects]);

  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);

  useEffect(() => {
    if (
      projectsLoadStatus === "succeeded" &&
      baseProjects &&
      Object.keys(baseProjects).length === 0
    ) {
      setIsWelcomeModalOpen(true);
    }
  }, [projectsLoadStatus, baseProjects]);

  const filteredProjects = useMemo(() => {
    return search
      ? projectList.filter(({ project }) => {
          const keysToSearch = ["name", "repoPath"];

          for (const key of keysToSearch) {
            if (project[key].toLowerCase().includes(search.toLowerCase())) {
              return true;
            }
          }
        })
      : projectList;
  }, [search, projectList]);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    [setSearch],
  );

  const handleChangeSelectedProject = useCallback(
    (projectId) => {
      setSearchParams((prev) => ({
        ...prev,
        project_id: projectId,
      }));
      clearSelection();
    },
    [clearSelection, setSearchParams],
  );

  if (!projectList.length) {
    return (
      <WelcomeModal
        open={isWelcomeModalOpen}
        onOpenChange={setIsWelcomeModalOpen}
      />
    );
  }

  return (
    <>
      <WelcomeModal
        open={isWelcomeModalOpen}
        onOpenChange={setIsWelcomeModalOpen}
      />
      <ProjectDeleteModal />
      <ProjectEditModal />
      <div className="max-w-(--breakpoint-xl) mx-auto px-8 box-content pb-8">
        <div className="border rounded-lg p-4 flex flex-col gap-4">
          <div className="flex items-center">
            <label className="font-medium flex h-8 gap-2 rounded-md cursor-pointer items-center text-xs focus:outline-hidden focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 bg-secondary px-2 hover:bg-secondary-200 focus:bg-secondary-300 focus:text-text-primary placeholder:text-text-muted">
              <Search className="w-3 h-3 text-text-muted" />
              <input
                className="bg-transparent"
                placeholder="Search projects"
                value={search}
                onChange={handleSearchChange}
                data-testid="search-projects-input"
              />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {filteredProjects.map(({ project, lastUpdated }) => (
              <ProjectCard
                key={project?.id}
                project={project}
                lastUpdated={lastUpdated}
                onClick={handleChangeSelectedProject}
              />
            ))}
            {!filteredProjects.length && (
              <div className="col-span-full text-center text-sm text-text-muted py-16">
                No projects found
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
